import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../components/common/SafeIcon';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { useAuth } from '../contexts/AuthContext';
import supabase from '../lib/supabase';
import toast from 'react-hot-toast';

const { FiMail, FiLock, FiAlertCircle, FiLoader } = FiIcons;

const AdminLoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { login } = useAuth();

  // Updated Logo URL
  const logoUrl = 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1753101906746-Cogli%20Caffe%20Side%20By%20Side.png';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // First attempt to log in
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) {
        throw signInError;
      }

      // Check if user is admin
      if (signInData && signInData.user) {
        console.log("User authenticated successfully:", signInData.user.id);
        
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', signInData.user.id)
          .single();

        if (profileError) {
          console.error("Profile fetch error:", profileError);
          throw new Error('Failed to fetch user profile');
        }

        if (profile && profile.role === 'admin') {
          toast.success('Admin login successful!');
          navigate('/admin');
        } else {
          setError('You do not have admin privileges');
          // Sign out if not admin
          await supabase.auth.signOut();
        }
      }
    } catch (err) {
      console.error('Error during admin login:', err);
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Admin Login - Cogli Caffe</title>
        <meta name="description" content="Admin login page for Cogli Caffe management" />
      </Helmet>
      <Header />
      <main className="min-h-screen bg-gradient-to-br from-coffee-50 to-cream-100 py-16">
        <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{opacity: 0, y: 20}} animate={{opacity: 1, y: 0}} className="bg-white rounded-lg shadow-lg p-8">
            <div className="text-center mb-8">
              <img src={logoUrl} alt="Cogli Caffe" className="h-16 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-coffee-800">Admin Login</h2>
              <p className="text-coffee-600 mt-2">Sign in to access admin panel</p>
            </div>

            {error && (
              <motion.div initial={{opacity: 0, height: 0}} animate={{opacity: 1, height: 'auto'}} className="bg-red-50 border-l-4 border-red-500 p-4 rounded mb-6">
                <div className="flex">
                  <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-red-500 mr-2" />
                  <p className="text-red-700">{error}</p>
                </div>
              </motion.div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-coffee-700 mb-1">
                  Email
                </label>
                <div className="relative">
                  <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-coffee-400" />
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="pl-10 w-full py-3 border border-coffee-200 rounded-lg focus:ring-2 focus:ring-coffee-500 focus:border-transparent"
                    placeholder="admin@example.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-coffee-700 mb-1">
                  Password
                </label>
                <div className="relative">
                  <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-coffee-400" />
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="pl-10 w-full py-3 border border-coffee-200 rounded-lg focus:ring-2 focus:ring-coffee-500 focus:border-transparent"
                    placeholder="••••••••"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center btn-coffee py-3"
              >
                {loading ? (
                  <>
                    <SafeIcon icon={FiLoader} className="animate-spin mr-2" />
                    Signing In...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <Link to="/" className="text-coffee-600 hover:text-coffee-800 font-medium">
                Back to Homepage
              </Link>
            </div>
          </motion.div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default AdminLoginPage;